import Model from "../../Model.js";

/**
 * A `LiquidityWeekReport` presents how much money is available in
 * a particular week to pay expenses (e.g., salaries and
 * invoices).
 *
 * @param {number} spec.weekNumber - Identifier for the reported week
 * @param {number} spec.endOfWeek - .
 * @param {number} spec.checkLimit - .
 * @param {number} spec.otherAdjustments -
 * @param {number} spec.startPaymentAbility -
 * @param {number} spec.incomingPayments -
 * @param {number} spec.outgoingPayments -
 * @param {number} spec.endPaymentAbility -
 * @param {number} spec.incomingAccountReceivablesNotOverdue -
 * @param {number} spec.incomingAccountReceivablesOverdue -
 * @param {number} spec.incomingAccountReceivablesTotal -
 * @param {number} spec.incomingWriteoffs -
 * @param {number} spec.incomingVat -
 * @param {number} spec.incomingSalesCash -
 * @param {number} spec.incomingNewLoans -
 * @param {number} spec.incomingOther -
 * @param {number} spec.outgoingAccountPayablesNotOverdue -
 * @param {number} spec.outgoingAccountPayablesOverdue -
 * @param {number} spec.outgoingAccountPayablesTotal -
 * @param {number} spec.outgoingWriteoffs -
 * @param {number} spec.outgoingUpcomingPayments -
 * @param {number} spec.outgoingAutogiroPayments -
 * @param {number} spec.outgoingSalaries -
 * @param {number} spec.outgoingWithholdingTaxes -
 * @param {number} spec.outgoingPayrollTaxes -
 * @param {number} spec.outgoingPreliminaryTaxes -
 * @param {number} spec.outgoingVat -
 * @param {number} spec.outgoingOther -
 */
export default class LiquidityWeekReport extends Model {
	constructor({
		weekNumber,
		endOfWeek,
		checkLimit,
		otherAdjustments,
		startPaymentAbility, // = endPaymentAbility of previous week
		incomingAccountReceivablesTotal, // = incomingAccountReceivablesNotOverdue + incomingAccountReceivablesOverdue
		outgoingAccountPayablesTotal, // = outgoingAccountPayablesNotOverdue + outgoingAccountPayablesOverdue
		incomingPayments, // = sum of incoming* below
		outgoingPayments, // = sum of outgoing* below
		endPaymentAbility, // = startPaymentAbility + incomingPayments - outgoingPayments
		incomingAccountReceivablesNotOverdue,
		incomingAccountReceivablesOverdue,
		incomingWriteoffs,
		incomingVat,
		incomingSalesCash,
		incomingNewLoans,
		incomingOther,
		outgoingAccountPayablesNotOverdue,
		outgoingAccountPayablesOverdue,
		outgoingWriteoffs,
		outgoingUpcomingPayments,
		outgoingAutogiroPayments,
		outgoingSalaries,
		outgoingWithholdingTaxes,
		outgoingPayrollTaxes,
		outgoingPreliminaryTaxes,
		outgoingVat,
		outgoingOther,
	}) {
		super(...arguments);
		this._weekNumber = weekNumber;
		this._endOfWeek = endOfWeek;
		this._checkLimit = checkLimit;
		this._otherAdjustments = otherAdjustments;
		this._startPaymentAbility = startPaymentAbility;
		this._incomingAccountReceivablesTotal = incomingAccountReceivablesTotal;
		this._outgoingAccountPayablesTotal = outgoingAccountPayablesTotal;
		this._incomingPayments = incomingPayments;
		this._outgoingPayments = outgoingPayments;
		this._endPaymentAbility = endPaymentAbility;

		this._incomingAccountReceivablesNotOverdue =
			incomingAccountReceivablesNotOverdue;

		this._incomingAccountReceivablesOverdue =
			incomingAccountReceivablesOverdue;

		this._incomingWriteoffs = incomingWriteoffs;
		this._incomingVat = incomingVat;
		this._incomingSalesCash = incomingSalesCash;
		this._incomingNewLoans = incomingNewLoans;
		this._incomingOther = incomingOther;

		this._outgoingAccountPayablesNotOverdue =
			outgoingAccountPayablesNotOverdue;

		this._outgoingAccountPayablesOverdue = outgoingAccountPayablesOverdue;
		this._outgoingWriteoffs = outgoingWriteoffs;
		this._outgoingUpcomingPayments = outgoingUpcomingPayments;
		this._outgoingAutogiroPayments = outgoingAutogiroPayments;
		this._outgoingSalaries = outgoingSalaries;
		this._outgoingWithholdingTaxes = outgoingWithholdingTaxes;
		this._outgoingPayrollTaxes = outgoingPayrollTaxes;
		this._outgoingPreliminaryTaxes = outgoingPreliminaryTaxes;
		this._outgoingVat = outgoingVat;
		this._outgoingOther = outgoingOther;
	}

	getWeekNumber() {
		return this._weekNumber;
	}

	getEndOfWeek() {
		return this._endOfWeek;
	}

	getCheckLimit() {
		return this._checkLimit;
	}

	getOtherAdjustments() {
		return this._otherAdjustments;
	}

	getStartPaymentAbility() {
		return this._startPaymentAbility;
	}

	getIncomingAccountReceivablesTotal() {
		return this._incomingAccountReceivablesTotal;
	}

	getOutgoingAccountPayablesTotal() {
		return this._outgoingAccountPayablesTotal;
	}

	getIncomingPayments() {
		return this._incomingPayments;
	}

	getOutgoingPayments() {
		return this._outgoingPayments;
	}

	getEndPaymentAbility() {
		return this._endPaymentAbility;
	}

	getIncomingAccountReceivablesNotOverdue() {
		return this._incomingAccountReceivablesNotOverdue;
	}

	getIncomingAccountReceivablesOverdue() {
		return this._incomingAccountReceivablesOverdue;
	}

	getIncomingWriteoffs() {
		return this._incomingWriteoffs;
	}

	getIncomingVat() {
		return this._incomingVat;
	}

	getIncomingSalesCash() {
		return this._incomingSalesCash;
	}

	getIncomingNewLoans() {
		return this._incomingNewLoans;
	}

	getIncomingOther() {
		return this._incomingOther;
	}

	getOutgoingAccountPayablesNotOverdue() {
		return this._outgoingAccountPayablesNotOverdue;
	}

	getOutgoingAccountPayablesOverdue() {
		return this._outgoingAccountPayablesOverdue;
	}

	getOutgoingWriteoffs() {
		return this._outgoingWriteoffs;
	}

	getOutgoingUpcomingPayments() {
		return this._outgoingUpcomingPayments;
	}

	getOutgoingAutogiroPayments() {
		return this._outgoingAutogiroPayments;
	}

	getOutgoingSalaries() {
		return this._outgoingSalaries;
	}

	getOutgoingWithholdingTaxes() {
		return this._outgoingWithholdingTaxes;
	}

	getOutgoingPayrollTaxes() {
		return this._outgoingPayrollTaxes;
	}

	getOutgoingPreliminaryTaxes() {
		return this._outgoingPreliminaryTaxes;
	}

	getOutgoingVat() {
		return this._outgoingVat;
	}

	getOutgoingOther() {
		return this._outgoingOther;
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.weekNumber = this._weekNumber;
		properties.endOfWeek = this._endOfWeek;
		properties.checkLimit = this._checkLimit;
		properties.otherAdjustments = this._otherAdjustments;
		properties.startPaymentAbility = this._startPaymentAbility;

		properties.incomingAccountReceivablesTotal =
			this._incomingAccountReceivablesTotal;

		properties.outgoingAccountPayablesTotal =
			this._outgoingAccountPayablesTotal;

		properties.incomingPayments = this._incomingPayments;
		properties.outgoingPayments = this._outgoingPayments;
		properties.endPaymentAbility = this._endPaymentAbility;

		properties.incomingAccountReceivablesNotOverdue =
			this._incomingAccountReceivablesNotOverdue;

		properties.incomingAccountReceivablesOverdue =
			this._incomingAccountReceivablesOverdue;

		properties.incomingWriteoffs = this._incomingWriteoffs;
		properties.incomingVat = this._incomingVat;
		properties.incomingSalesCash = this._incomingSalesCash;
		properties.incomingNewLoans = this._incomingNewLoans;
		properties.incomingOther = this._incomingOther;

		properties.outgoingAccountPayablesNotOverdue =
			this._outgoingAccountPayablesNotOverdue;

		properties.outgoingAccountPayablesOverdue =
			this._outgoingAccountPayablesOverdue;

		properties.outgoingWriteoffs = this._outgoingWriteoffs;
		properties.outgoingUpcomingPayments = this._outgoingUpcomingPayments;
		properties.outgoingAutogiroPayments = this._outgoingAutogiroPayments;
		properties.outgoingSalaries = this._outgoingSalaries;
		properties.outgoingWithholdingTaxes = this._outgoingWithholdingTaxes;
		properties.outgoingPayrollTaxes = this._outgoingPayrollTaxes;
		properties.outgoingPreliminaryTaxes = this._outgoingPreliminaryTaxes;
		properties.outgoingVat = this._outgoingVat;
		properties.outgoingOther = this._outgoingOther;

		return properties;
	}
}
